import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import i18n from '@/i18n/config';
import { getLocalizedValue, getLocalizedArray } from '@/i18n/utils';

// 自定義開關的前端顯示格式
export interface PublicCustomToggle {
  key: string;
  enabled: boolean;
  label: string; // 已本地化的標籤
}

export interface PublicPlan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  monthlyPrice: number | null;
  yearlyPrice: number | null;
  yearlyDiscountPercent: number;
  lifetimePrice: number | null;
  features: string[];
  limits: Record<string, unknown>;
  customToggles: PublicCustomToggle[];
  colorClass: string;
  isPopular: boolean;
  ctaText: string;
  sortOrder: number;
  monthlyStripePriceId: string | null;
  yearlyStripePriceId: string | null;
  lifetimeStripePriceId: string | null;
}

// 可用的顏色選項（用於管理後台）
import { PLAN_COLORS } from '@/lib/constants';

// 可用的顏色選項（用於管理後台）
export { PLAN_COLORS };



// 格式化自定義開關
function formatCustomToggles(
  toggles: Array<{ key: string; enabled: boolean; labels: Record<string, string> }> | null
): PublicCustomToggle[] {
  if (!toggles || !Array.isArray(toggles)) return [];
  
  return toggles
    .filter(t => t.enabled) // 只顯示啟用的開關
    .map(t => ({
      key: t.key,
      enabled: t.enabled,
      label: getLocalizedValue(t.labels, t.key),
    }));
}

// 格式化 bytes 為人類可讀的字串
function formatBytes(bytes: number): string {
  if (bytes === -1) return '∞';
  if (bytes >= 1073741824) { // 1 GB
    return `${(bytes / 1073741824).toFixed(0)} GB`;
  }
  if (bytes >= 1048576) { // 1 MB
    return `${(bytes / 1048576).toFixed(0)} MB`;
  }
  return `${(bytes / 1024).toFixed(0)} KB`;
}

// 動態生成儲存相關功能文字
function generateStorageFeature(limits: Record<string, unknown>, lang: string): string | null {
  const totalBytes = limits.cloudStorageTotalBytes as number | undefined;
  if (!totalBytes || totalBytes <= 0) return null;
  
  const formattedSize = formatBytes(totalBytes);
  
  const translations: Record<string, string> = {
    'zh-TW': `${formattedSize} 雲端儲存`,
    'en': `${formattedSize} cloud storage`,
    'ja': `${formattedSize} クラウドストレージ`,
    'zh-CN': `${formattedSize} 云端存储`,
    'ko': `${formattedSize} 클라우드 스토리지`,
  };
  
  return translations[lang] || translations['en'];
}

// 動態生成腳本數量限制文字
function generateScriptsFeature(limits: Record<string, unknown>, lang: string): string | null {
  const maxScripts = limits.maxScripts as number | undefined;
  if (maxScripts === undefined) return null;
  
  if (maxScripts === -1) {
    const translations: Record<string, string> = {
      'zh-TW': '無限講稿',
      'en': 'Unlimited scripts',
      'ja': '無制限のスクリプト',
      'zh-CN': '无限讲稿',
      'ko': '무제한 스크립트',
    };
    return translations[lang] || translations['en'];
  }
  
  const translations: Record<string, string> = {
    'zh-TW': `最多 ${maxScripts} 份講稿`,
    'en': `Up to ${maxScripts} scripts`,
    'ja': `最大${maxScripts}スクリプト`,
    'zh-CN': `最多 ${maxScripts} 份讲稿`,
    'ko': `최대 ${maxScripts}개 스크립트`,
  };
  return translations[lang] || translations['en'];
}

// 動態生成字數限制文字
function generateCharsFeature(limits: Record<string, unknown>, lang: string): string | null {
  const maxChars = limits.maxCharactersPerScript as number | undefined;
  if (maxChars === undefined) return null;
  
  if (maxChars === -1) {
    const translations: Record<string, string> = {
      'zh-TW': '無限字數',
      'en': 'Unlimited characters',
      'ja': '無制限の文字数',
      'zh-CN': '无限字数',
      'ko': '무제한 글자 수',
    };
    return translations[lang] || translations['en'];
  }
  
  const formattedNum = new Intl.NumberFormat().format(maxChars);
  const translations: Record<string, string> = {
    'zh-TW': `每份講稿 ${formattedNum} 字`,
    'en': `${formattedNum} chars per script`,
    'ja': `スクリプトあたり${formattedNum}文字`,
    'zh-CN': `每份讲稿 ${formattedNum} 字`,
    'ko': `스크립트당 ${formattedNum}자`,
  };
  return translations[lang] || translations['en'];
}

// 合併靜態 features 和動態生成的 limits 文字
function mergeFeatures(
  staticFeatures: string[],
  limits: Record<string, unknown>,
  lang: string
): string[] {
  const dynamicFeatures: string[] = [];
  
  // 添加腳本數量限制
  const scriptsFeature = generateScriptsFeature(limits, lang);
  if (scriptsFeature) dynamicFeatures.push(scriptsFeature);
  
  // 添加字數限制
  const charsFeature = generateCharsFeature(limits, lang);
  if (charsFeature) dynamicFeatures.push(charsFeature);
  
  // 添加儲存限制
  const storageFeature = generateStorageFeature(limits, lang);
  if (storageFeature) dynamicFeatures.push(storageFeature);
  
  // 過濾掉靜態 features 中已被動態覆蓋的內容
  const filteredStatic = staticFeatures.filter(feature => {
    const lower = feature.toLowerCase();
    // 過濾掉包含腳本、字數、儲存相關的舊文字
    if (lower.includes('script') || lower.includes('講稿') || lower.includes('讲稿') || lower.includes('スクリプト')) return false;
    if (lower.includes('char') || lower.includes('字數') || lower.includes('字数') || lower.includes('文字')) return false;
    if (lower.includes('storage') || lower.includes('儲存') || lower.includes('存储') || lower.includes('ストレージ') || lower.includes('스토리지')) return false;
    return true;
  });
  
  // 動態優先，然後是過濾後的靜態
  return [...dynamicFeatures, ...filteredStatic];
}

export function usePublicPlans() {
  const [plans, setPlans] = useState<PublicPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (fetchError) throw fetchError;

      const currentLang = i18n.language;
      
      const formattedPlans: PublicPlan[] = (data || []).map((plan) => {
        const limits = (plan.limits as Record<string, unknown>) || {};
        const staticFeatures = getLocalizedArray(plan.features as Record<string, string[]>);
        
        return {
          id: plan.id,
          name: plan.name,
          displayName: getLocalizedValue(plan.display_name as Record<string, string>, plan.name),
          description: getLocalizedValue(plan.description as Record<string, string>, ''),
          monthlyPrice: plan.monthly_price,
          yearlyPrice: plan.yearly_price,
          yearlyDiscountPercent: plan.yearly_discount_percent || 20,
          lifetimePrice: plan.lifetime_price,
          features: mergeFeatures(staticFeatures, limits, currentLang),
          limits: limits,
          customToggles: formatCustomToggles(
            plan.custom_toggles as Array<{ key: string; enabled: boolean; labels: Record<string, string> }> | null
          ),
          colorClass: plan.color_class || 'bg-accent-yellow',
          isPopular: plan.is_popular || false,
          ctaText: getLocalizedValue(plan.cta_text as Record<string, string>, 'Get Started'),
          sortOrder: plan.sort_order || 0,
          monthlyStripePriceId: plan.monthly_stripe_price_id,
          yearlyStripePriceId: plan.yearly_stripe_price_id,
          lifetimeStripePriceId: plan.lifetime_stripe_price_id,
        };
      });

      setPlans(formattedPlans);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch plans';
      setError(message);
      console.error('[usePublicPlans] Error:', message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // 當語言變化時重新格式化
  useEffect(() => {
    const handleLanguageChange = () => {
      fetchPlans();
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [fetchPlans]);

  return { plans, loading, error, refetch: fetchPlans };
}
